import { useEffect, useState } from "react";
import { StagePill, TempPill } from "@/components/leads/LeadPipelineBoard";
import { FileText, Check, Wrench, ListTodo, CalendarClock, RefreshCw, Ban, Zap, Package, Wallet, AlertTriangle, Mail, CircleDollarSign } from "lucide-react";
import { getDashboardStats, getPriorityActions, getDashboardReminders, getLeadFollowUps, updateLeadFollowUp, updateLead, convertLead, getAnnualServiceDue, getScheduledWorkOrders, getPaymentHolds, getImmediateActions, getProposalNextActions, getInspectionDeficiencies, getInspectionsToSend, createProposalFromDeficiencies, createWOFromDeficiencies, getUnpaidInvoices, getPendingInvoices, getFailedEmails, retryFailedEmail, updateInvoiceStatus, getARPmAlerts, getProjectManagers, getInspections, getPartsToShip, exportPartsOrderToFoundation, addDashboardDismissal, removeDashboardDismissal } from "@/lib/api";
import { PartsStatusButton } from "@/components/PartsStatusButton";
import { useSectionCollapse, CollapseContext } from "@/components/common/ExpandCollapseAll";
import { PillRail, Pill } from "@/components/common/Segmented";

const OPS_OPEN = {};
const failedEmailError = (fe) => {
  const raw = fe.status === "skipped_no_recipient" ? "No email address on file" : (fe.bounce_message || fe.error || "");
  return raw.length > 80 ? `${raw.slice(0, 77)}…` : raw;
};
import FieldCompletionsPanel from "@/components/FieldCompletionsPanel";
import { downloadPdfUrl } from "@/lib/downloadBlob";
import { COMPANY_INFO } from "@/lib/companyInfo";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import useRefreshTick from "@/hooks/useRefreshTick";
import usePageScrollLane from "@/hooks/usePageScrollLane";
import { useAuth } from "@/auth/AuthContext";
import { InvoiceActionButtons, sendInvoiceReminder, PaymentDialog } from "@/components/invoicing/InvoiceActions";
import { InvoiceNotesCell } from "@/components/invoicing/ARCells";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [priorityActions, setPriorityActions] = useState([]);

  const [reminders, setReminders] = useState([]);
  const [leadFollowUps, setLeadFollowUps] = useState([]);

  const [annualServiceDue, setAnnualServiceDue] = useState([]);
  const [scheduledWorkOrders, setScheduledWorkOrders] = useState([]);
  const [paymentHolds, setPaymentHolds] = useState([]);
  const [immediateActions, setImmediateActions] = useState([]);
  // Completed inspections the PM still has to review + send to the customer.
  const [inspectionsToSend, setInspectionsToSend] = useState([]);
  const [proposalNextActions, setProposalNextActions] = useState([]);
  const [inspectionDeficiencies, setInspectionDeficiencies] = useState([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState({ "0_30": [], "31_60": [], "60_plus": [] });
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [arPmAlerts, setArPmAlerts] = useState([]);
  const [failedEmails, setFailedEmails] = useState([]);
  const { user } = useAuth();
  const [partsToShip, setPartsToShip] = useState([]);
  const [showDismissedDef, setShowDismissedDef] = useState(false);
  const loadDeficiencies = (includeDismissed) => getInspectionDeficiencies(includeDismissed ? { include_dismissed: true } : undefined).then(r => setInspectionDeficiencies(r.data || [])).catch(() => {});
  const [followUpAction, setFollowUpAction] = useState(null); // { lead, type: "done" | "convert" }
  const [followUpActionNotes, setFollowUpActionNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const collapsedSections = OPS_OPEN;
  const toggleSection = () => {};
  const [filterPerson, setFilterPerson] = useState("all"); // "all" or a name string
  const [people, setPeople] = useState([]); // PM names (Settings → Project Managers)

  const refreshTick = useRefreshTick();
  const laneRef = usePageScrollLane(0.25);
  useEffect(() => {
    loadDashboardData();
    if (refreshTick) loadDeficiencies(showDismissedDef);
  }, [refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDashboardData = async () => {
    try {
      const [statsRes, actionsRes, remindersRes, followUpsRes, annualRes, scheduledRes, paymentHoldsRes, immediateActionsRes, inspectionsToSendRes, proposalNextActionsRes, deficienciesRes, unpaidInvRes, pendingInvRes, arPmAlertsRes, pmRes, partsShipRes] = await Promise.all([
        getDashboardStats(),
        getPriorityActions(),
        getDashboardReminders(),
        getLeadFollowUps(),
        getAnnualServiceDue(),
        getScheduledWorkOrders(),
        getPaymentHolds(),
        getImmediateActions(),
        getInspectionsToSend(),
        getProposalNextActions(),
        getInspectionDeficiencies(),
        getUnpaidInvoices(),
        getPendingInvoices(),
        getARPmAlerts(),
        getProjectManagers().catch(() => ({ data: [] })),
        getPartsToShip().catch(() => ({ data: [] })),
      ]);
      getFailedEmails().then(r => setFailedEmails(r.data?.items || [])).catch(() => {});
      setStats(statsRes.data);
      setPriorityActions(actionsRes.data);
      setReminders(remindersRes.data);
      setLeadFollowUps(followUpsRes.data);
      setAnnualServiceDue(annualRes.data);
      setScheduledWorkOrders(scheduledRes.data);
      setPaymentHolds(paymentHoldsRes.data);
      setImmediateActions(immediateActionsRes.data);
      setInspectionsToSend(inspectionsToSendRes.data || []);
      setProposalNextActions(proposalNextActionsRes.data);
      setInspectionDeficiencies(deficienciesRes.data);
      setUnpaidInvoices(unpaidInvRes.data?.buckets || unpaidInvRes.data || { "0_30": [], "31_60": [], "60_plus": [] });
      setPendingInvoices(pendingInvRes.data || []);
      setArPmAlerts(arPmAlertsRes.data || []);
      setPartsToShip(partsShipRes.data || []);
      // Pills are PMs only — sourced from Settings → Project Managers
      const pmNames = [...new Set((pmRes.data || []).filter(p => p.active !== false).map(p => p.name).filter(Boolean))].sort();
      setPeople(pmNames);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const openFollowUpAction = (lead, type) => {
    setFollowUpAction({ lead, type });
    setFollowUpActionNotes(lead.follow_up_notes || "");
  };

  const handleFollowUpAction = async () => {
    if (!followUpAction) return;
    const { lead, type } = followUpAction;
    try {
      // Append follow-up note to lead's notes array
      const existingNotes = Array.isArray(lead.notes) ? lead.notes : [];
      const updatedNotes = followUpActionNotes.trim()
        ? [...existingNotes, { date: new Date().toISOString().slice(0, 10), text: followUpActionNotes.trim() }]
        : existingNotes;
      
      await updateLeadFollowUp(lead.id, {
        next_follow_up: lead.next_follow_up,
        follow_up_notes: followUpActionNotes,
        follow_up_complete: true,
      });
      // Also save the note to the lead's notes array, preserving follow_up_complete
      await updateLead(lead.id, { ...lead, notes: updatedNotes, follow_up_complete: true, next_follow_up: null });
      
      if (type === "convert") {
        const res = await convertLead(lead.id);
        const newClientId = res.data?.id;
        setLeadFollowUps(prev => prev.filter(l => l.id !== lead.id));
        setFollowUpAction(null);
        toast.success("Lead converted to customer");
        navigate('/clients', { state: { openClientId: newClientId } });
      } else {
        setLeadFollowUps(prev => prev.filter(l => l.id !== lead.id));
        setFollowUpAction(null);
        toast.success("Follow-up complete");
      }
    } catch (error) {
      toast.error(type === "convert" ? "Failed to convert lead" : "Failed to update follow-up");
    }
  };

  const handleSendInvoice = async (inv) => {
    // 1. Download invoice PDF from server
    downloadPdfUrl(`/invoices/${inv.id}/pdf?download=1`, {
      suggestedName: `ABS_Invoice_${inv.invoice_number || inv.id}.pdf`,
      label: "invoice",
    });

    // 2. Check for completed inspection on this WO and download that too
    let inspAttached = false;
    try {
      if (inv.work_order_id) {
        const inspRes = await getInspections({ work_order_id: inv.work_order_id });
        const completed = (inspRes.data || []).filter(i => i.status === "complete");
        if (completed.length > 0) {
          const insp = completed[0];
          setTimeout(() => downloadPdfUrl(`/inspections/${insp.id}/pdf?download=1`, {
            suggestedName: `ABS_Inspection_${insp.id}.pdf`,
            label: "inspection",
          }), 500);
          inspAttached = true;
        }
      }
    } catch (err) { console.error("Inspection PDF error:", err); }

    // 3. Open mailto
    const to = inv.customer_email || "";
    const subject = encodeURIComponent(`Invoice ${inv.invoice_number} - ${inv.customer_name || ""}`);
    const attachNote = inspAttached ? "\n\nAlso attached is the completed inspection report for your records." : "";
    const body = encodeURIComponent(
      `Hi ${inv.customer_contact || ""},\n\nPlease find attached Invoice ${inv.invoice_number} for ${inv.wo_number ? `WO# ${inv.wo_number}` : "services rendered"} in the amount of ${formatCurrency(inv.total || 0)}.${attachNote}\n\nPlease let us know if you have any questions.\n\nThank you,\nABS Inc.\n303-293-2096`
    );
    setTimeout(() => window.open(`mailto:${to}?subject=${subject}&body=${body}`, "_blank"), inspAttached ? 1000 : 200);

    // 4. Auto-mark as sent (PATCH — the generic PUT drops `status`)
    try {
      await updateInvoiceStatus(inv.id, "sent");
      toast.success(`Invoice ${inv.invoice_number} marked as sent${inspAttached ? " (inspection report also downloaded)" : ""}`);
      loadDashboardData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update invoice status"); }
  };

  const patchUnpaid = (id, patch) => setUnpaidInvoices(prev => Object.fromEntries(Object.entries(prev).map(([k, list]) => [k, Array.isArray(list) ? list.map(i => i.id === id ? { ...i, ...patch } : i) : list])));
  const resendFailedEmail = async (fe) => {
    const to = window.prompt(`Resend "${fe.subject || "email"}" to:`, fe.to || "");
    if (to === null) return;
    if (!to.includes("@")) { toast.error("Enter a valid email"); return; }
    try {
      const res = await retryFailedEmail(fe.id, { new_email: to.trim(), propagate: false });
      const st = res.data?.retry_status;
      if (st === "error") { toast.error(`Resend failed: ${res.data?.error || "unknown"}`); return; }
      if (st === "skipped") { toast.error("Resend not sent — email automation is turned off"); return; }
      toast.success(`Email resent to ${to.trim()}`);
      setFailedEmails(prev => prev.filter(x => x.id !== fe.id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Resend failed"); }
  };

  const handleSendReminder = async (inv) => {
    const res = await sendInvoiceReminder(inv);
    if (res) patchUnpaid(inv.id, { last_reminder_tier: res.tier, last_reminder_at: res.sent_at, ar_last_action: null });
  };
  const [paymentTarget, setPaymentTarget] = useState(null);
  const handleMarkPaid = async (inv) => {
    try {
      await updateInvoiceStatus(inv.id, "paid");
      toast.success(`Invoice ${inv.invoice_number} marked as paid`);
      // Remove from PM alerts and reload
      setArPmAlerts(prev => prev.filter(a => a.id !== inv.id));
      loadDashboardData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to mark invoice as paid"); }
  };

  const buildAllActions = () => {
    const actions = [];
    const seenIds = new Set();

    // AR PM Alerts (35+ days overdue invoices) — HIGHEST PRIORITY
    arPmAlerts.forEach((alert) => {
      const key = `ar-pm-${alert.id}`;
      if (seenIds.has(key)) return;
      seenIds.add(key);
      const isHold = alert.age_days >= 60;
      actions.push({
        id: key,
        type: 'ar_pm_alert',
        title: `${isHold ? 'Account Hold' : 'Past Due'}: Invoice ${alert.invoice_number}`,
        subtitle: `${alert.client_name} — ${formatCurrency(alert.total)} — ${alert.age_days} days${isHold ? ' — ON HOLD' : ''}`,
        urgency: isHold ? 'overdue' : (alert.age_days >= 45 ? 'overdue' : 'due_soon'),
        dueDate: alert.sent_at || alert.created_at,
        nextActionLabel: 'Mark Paid',
        invoiceData: alert,
        navigate: () => navigate('/clients', { state: { openClientId: alert.client_id, openTab: 'workorders', ...(alert.work_order_id ? { openWorkOrderId: alert.work_order_id } : {}) } }),
      });
    });

    // Failed emails — bare bones: to, subject, short error, Resend.
    failedEmails.forEach((fe) => {
      const key = `failed-email-${fe.id}`;
      if (seenIds.has(key)) return;
      seenIds.add(key);
      const err = failedEmailError(fe);
      actions.push({
        id: key,
        type: 'failed_email',
        title: `Failed email: ${fe.subject || "(no subject)"}`,
        subtitle: `To: ${fe.to || "none"}${err ? ` — ${err}` : ""}`,
        urgency: 'overdue',
        dueDate: fe.created_at || fe.sent_at,
        nextActionLabel: 'Resend email',
        onAction: () => resendFailedEmail(fe),
        navigate: () => { if (fe.source?.url) navigate(fe.source.url); },
      });
    });

    // Inspections a tech has completed that haven't gone to the customer
    // yet. Clears itself the moment the report is emailed from the WO.
    inspectionsToSend.forEach((insp) => {
      const key = `insp-send-${insp.inspection_id}`;
      if (seenIds.has(key)) return;
      seenIds.add(key);
      const defs = insp.deficiency_count || 0;
      actions.push({
        id: key,
        type: 'inspection_to_send',
        title: `Inspection completed: ${insp.template_name}`,
        subtitle: [
          insp.client_name,
          insp.wo_number ? `WO ${insp.wo_number}` : '',
          defs ? `${defs} deficienc${defs === 1 ? 'y' : 'ies'}` : 'no deficiencies',
          insp.inspector_name ? `by ${insp.inspector_name}` : '',
          insp.needs_review ? 'NEEDS REVIEW — two versions' : '',
        ].filter(Boolean).join(' — '),
        urgency: (defs > 0 || insp.needs_review) ? 'overdue' : 'due_soon',
        dueDate: insp.completed_at || insp.conducted_date,
        nextActionLabel: 'Review & Send',
        navigate: () => navigate('/clients', { state: { openClientId: insp.client_id, openTab: 'workorders', ...(insp.work_order_id ? { openWorkOrderId: insp.work_order_id } : {}) } }),
      });
    });

    // iter237 — Lead follow-ups moved to the Sales Dashboard (kept out of
    // the Ops Dashboard priority list intentionally).

    // Reminders (proposals, equipment, etc.)
    reminders.forEach((r) => {
      if (seenIds.has(r.id)) return;
      // iter237 — lead/proposal reminder types live on the Sales Dashboard now.
      if (["proposal_draft", "proposal_followup", "lead_followup"].includes(r.type)) return;
      seenIds.add(r.id);
      let nextActionLabel = 'View';
      let nav = () => {};
      let titlePrefix = '';
      if (r.type === 'proposal_draft') {
        nextActionLabel = 'Submit Proposal';
        titlePrefix = 'Submit: Proposal: ';
        nav = () => navigate('/clients', { state: { openClientId: r.client_id, openTab: 'proposals' } });
      } else if (r.type === 'proposal_followup') {
        nextActionLabel = 'Follow Up';
        titlePrefix = 'Proposal Follow Up: ';
        nav = () => navigate('/clients', { state: { openClientId: r.client_id, openTab: 'proposals' } });
      } else if (r.type === 'lead_followup') {
        nextActionLabel = 'Follow Up';
        titlePrefix = 'Follow Up: Lead: ';
        nav = () => navigate('/leads');
      } else if (r.type === 'equipment_service') {
        nextActionLabel = 'Schedule Service';
        titlePrefix = 'Service Due: Equipment: ';
        nav = () => navigate('/clients', { state: { openClientId: r.client_id, openTab: 'equipment' } });
      } else if (r.type === 'work_order_scheduled') {
        nextActionLabel = 'Schedule Work';
        titlePrefix = 'Schedule: Work Order: ';
        nav = () => navigate('/clients', { state: { openClientId: r.client_id, openTab: 'workorders', openWorkOrderId: r.id } });
      } else if (r.type === 'work_order_submitted') {
        nextActionLabel = 'Follow Up';
        titlePrefix = 'Follow Up: Work Order: ';
        nav = () => navigate('/clients', { state: { openClientId: r.client_id, openTab: 'workorders', openWorkOrderId: r.id } });
      } else if (r.type === 'work_order_approved') {
        nextActionLabel = 'Get Signature';
        titlePrefix = 'Sign Off: Work Order: ';
        nav = () => navigate('/clients', { state: { openClientId: r.client_id, openTab: 'workorders', openWorkOrderId: r.id } });
      }
      actions.push({
        id: r.id,
        type: r.type,
        title: titlePrefix ? `${titlePrefix}${r.title}` : r.title,
        subtitle: r.client_name + (r.subtitle ? ` - ${r.subtitle}` : ''),
        urgency: r.urgency,
        dueDate: r.due_date,
        nextActionLabel,
        navigate: nav,
      });
    });

    // Priority actions (jobs, hot leads)
    priorityActions.forEach((a) => {
      if (seenIds.has(a.id)) return;
      seenIds.add(a.id);
      let nextActionLabel = 'View';
      let nav = () => {};
      let urgency = 'upcoming';
      if (a.type === 'job') {
        nextActionLabel = 'View Job';
        nav = () => navigate('/jobs');
        if (a.due_date && new Date(a.due_date) < new Date()) urgency = 'overdue';
        else if (a.due_date) urgency = 'due_soon';
      } else if (a.type === 'lead') {
        nextActionLabel = 'Follow Up';
        nav = () => navigate('/leads');
        urgency = 'due_soon';
      }
      actions.push({
        id: a.id,
        type: a.type,
        title: a.title,
        subtitle: a.client_name,
        urgency,
        dueDate: a.due_date,
        nextActionLabel,
        navigate: nav,
      });
    });

    // iter237 — Proposal next actions moved to the Sales Dashboard.
    void proposalNextActions;

    // Sort: overdue first, then due_soon, then upcoming; within each group sort by date
    const urgencyOrder = { overdue: 0, due_soon: 1, upcoming: 2 };
    actions.sort((a, b) => {
      const ua = urgencyOrder[a.urgency] ?? 3;
      const ub = urgencyOrder[b.urgency] ?? 3;
      if (ua !== ub) return ua - ub;
      const da = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
      const db = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
      return da - db;
    });

    return actions;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const allActions = buildAllActions();


  const allUnpaid = [...(unpaidInvoices["0_30"] || []), ...(unpaidInvoices["31_60"] || []), ...(unpaidInvoices["60_plus"] || [])];

  return (
    <CollapseContext.Provider value={{ collapsed: collapsedSections, toggle: toggleSection }}>
    <div ref={laneRef} className="space-y-4" data-testid="dashboard-page">

      <div className="flex items-center gap-2 flex-wrap" data-testid="dashboard-header">
        {people.length > 0 && (
          <PillRail testId="dashboard-filter">
            <Pill on={filterPerson === "all"} onClick={() => setFilterPerson("all")} testId="filter-person-all">All PMs</Pill>
            {people.map(p => (
              <Pill key={p} on={filterPerson === p} onClick={() => setFilterPerson(p)} testId={`filter-person-${p.split(" ")[0]}`}>{p.split(" ")[0]}</Pill>
            ))}
          </PillRail>
        )}
      </div>




      <div className="dash-row grid-cols-1 lg:grid-cols-2" data-testid="dash-row-today">
        <div className="flex flex-col gap-4 min-w-0">
        <div className="h-[288px] empty:hidden"><FieldCompletionsPanel /></div>
        <div className="h-[288px]"><InspectionDeficienciesSection deficiencies={inspectionDeficiencies} navigate={navigate} showDismissed={showDismissedDef} onToggleDismissed={() => { const n = !showDismissedDef; setShowDismissedDef(n); loadDeficiencies(n); }} onReload={() => loadDeficiencies(showDismissedDef)} /></div>
        </div>
        <div className="relative min-h-[592px]">
          <div className="lg:absolute lg:inset-0">
        <div className="priority-list h-full flex flex-col" data-testid="priority-actions">
          <div className="priority-list-header">
            <ListTodo className="w-4 h-4 text-rose-500" />
            <h3 className="priority-list-title">Priority Actions</h3>
            {allActions.length > 0 && (
              <span className="ml-2 text-xs font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{allActions.length}</span>
            )}
          </div>
          
          {!collapsedSections.priorityActions && (allActions.length === 0 ? (
            <div className="empty-state">
              <p className="empty-state-title">All caught up!</p>
              <p className="empty-state-text">No pending priority actions</p>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto">
            {allActions.map((action, index) => (
              <div key={`${action.type}-${action.id}`} className={`priority-item ${action.urgency === 'overdue' ? 'bg-red-50' : ''}`} data-testid={`priority-item-${index}`}>
                <div
                  className="flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={action.navigate}
                  data-testid={`priority-action-link-${index}`}
                >
                  <div className="priority-item-title text-blue-700 hover:underline">{action.title}</div>
                  <div className="priority-item-subtitle">
                    {action.subtitle}
                    {action.dueDate && (
                      <span className="ml-1 text-slate-400">- {format(new Date(action.dueDate), 'MMM d, yyyy')}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${
                    action.urgency === 'overdue' ? 'bg-red-100 text-red-700' :
                    action.urgency === 'due_soon' ? 'bg-orange-100 text-orange-700' :
                    'bg-blue-100 text-blue-700'
                  }`} data-testid={`priority-status-${index}`}>
                    {action.urgency === 'overdue' ? 'Overdue' : action.urgency === 'due_soon' ? 'Due Soon' : 'Upcoming'}
                  </span>
                  {action.type === 'lead_followup' && action.leadData ? (
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={(e) => { e.stopPropagation(); openFollowUpAction(action.leadData, "done"); }}
                        data-testid={`priority-done-${index}`}
                      >
                        Done
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={(e) => { e.stopPropagation(); openFollowUpAction(action.leadData, "convert"); }}
                        data-testid={`priority-convert-${index}`}
                      >
                        Convert
                      </Button>
                    </div>
                  ) : action.type === 'ar_pm_alert' && action.invoiceData ? (
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs text-green-700 border-green-200 hover:bg-green-50"
                        onClick={(e) => { e.stopPropagation(); handleMarkPaid(action.invoiceData); }}
                        data-testid={`priority-mark-paid-${index}`}
                      >
                        Mark Paid
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs whitespace-nowrap"
                        onClick={(e) => { e.stopPropagation(); action.navigate(); }}
                        data-testid={`priority-view-invoice-${index}`}
                      >
                        View
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs whitespace-nowrap"
                      onClick={(e) => { e.stopPropagation(); (action.onAction || action.navigate)(); }}
                      data-testid={`priority-next-action-${index}`}
                    >
                      {action.nextActionLabel}
                    </Button>
                  )}
                </div>
              </div>
            ))}
            </div>
          ))}
        </div>
          </div>
        </div>
      </div>

      <div className="dash-row grid-cols-1 lg:grid-cols-2" data-testid="dash-row-work">
        <div className="priority-list !h-[320px]" data-testid="scheduled-work-orders">
          <div className="priority-list-header">
            <CalendarClock className="w-4 h-4 text-indigo-500" />
            <h3 className="priority-list-title">Scheduled Work Orders</h3>
            {scheduledWorkOrders.length > 0 && (
              <span className="ml-2 text-xs font-medium bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">{scheduledWorkOrders.length}</span>
            )}
          </div>
          {!collapsedSections.scheduledWOs && (scheduledWorkOrders.length === 0 ? (
            <div className="empty-state">
              <p className="empty-state-title">No scheduled work orders</p>
              <p className="empty-state-text">Work orders with scheduled dates will appear here</p>
            </div>
          ) : (
            <div className="dash-scroll">
            {scheduledWorkOrders.map((wo, index) => (
              <div key={wo.id} className={`priority-item ${wo.is_past_due ? 'bg-red-50' : ''}`}
                data-testid={`scheduled-wo-${index}`}>
                <div
                  className="flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => navigate('/clients', { state: { openClientId: wo.client_id, openTab: 'workorders', openWorkOrderId: wo.id } })}
                >
                  <div className="priority-item-title text-blue-700 hover:underline truncate">
                    {wo.client_name} - {wo.title}
                  </div>
                  <div className="priority-item-subtitle truncate">
                    WO# {wo.wo_number} &bull; {wo.site_name} {wo.equipment !== '-' ? `\u2022 ${wo.equipment}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-xs font-medium text-slate-600 num">{formatCurrency(wo.total)}</div>
                  <div className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap num ${
                    wo.is_past_due ? 'bg-red-200 text-red-800' : 'bg-indigo-100 text-indigo-700'
                  }`}>
                    {wo.scheduled_date ? format(new Date(wo.scheduled_date), 'MMM d') : 'No date'}
                  </div>
                </div>
              </div>
            ))}
            </div>
          ))}
        </div>
        <div className="priority-list !h-[320px]" data-testid="annual-service-due">
          <div className="priority-list-header">
            <RefreshCw className="w-4 h-4 text-purple-500" />
            <h3 className="priority-list-title">11-Month Service Due</h3>
            {annualServiceDue.length > 0 && (
              <span className="ml-2 text-xs font-medium bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">{annualServiceDue.length}</span>
            )}
          </div>
          {!collapsedSections.annualService && (annualServiceDue.length === 0 ? (
            <div className="empty-state">
              <p className="empty-state-title">No upcoming annual services</p>
              <p className="empty-state-text">Completed work orders with annual service checked appear here at the 11-month mark</p>
            </div>
          ) : (
            <div className="dash-scroll">
            {annualServiceDue.map((item, index) => (
              <div key={item.id} className={`priority-item ${item.is_overdue ? 'bg-red-50' : ''}`} data-testid={`annual-service-item-${index}`}>
                <div
                  className="flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => navigate('/clients', { state: { openClientId: item.client_id, openTab: 'workorders' } })}
                  data-testid={`annual-service-link-${index}`}
                >
                  <div className="priority-item-title text-blue-700 hover:underline truncate">{item.client_name}</div>
                  <div className="priority-item-subtitle truncate">
                    {item.equipment}
                    <span className="ml-1 text-slate-400">- Ref: WO# {item.wo_number}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-xs font-medium text-slate-700 num">{format(new Date(item.due_date), 'MMM d, yyyy')}</div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${
                    item.is_overdue ? 'bg-red-100 text-red-700' :
                    item.days_until_due <= 30 ? 'bg-orange-100 text-orange-700' :
                    'bg-purple-100 text-purple-700'
                  }`} data-testid={`annual-service-status-${index}`}>
                    {item.is_overdue ? 'Overdue' : item.days_until_due <= 30 ? 'Due Soon' : 'Upcoming'}
                  </span>
                </div>
              </div>
            ))}
            </div>
          ))}
        </div>
      </div>

      <div className="dash-row grid-cols-1 lg:grid-cols-[repeat(auto-fit,minmax(380px,1fr))] empty:hidden" data-testid="dash-row-holds">
      {paymentHolds.length > 0 && (
        <div className="priority-list border-red-200 !h-[260px]" data-testid="payment-hold-dashboard">
            <div className="priority-list-header">
              <Ban className="w-4 h-4 text-red-500" />
              <h3 className="priority-list-title text-red-700">Payment Hold</h3>
              <span className="ml-2 text-xs font-medium bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{paymentHolds.length}</span>
            </div>
            {!collapsedSections.paymentHold && (
            <div className="p-2 space-y-1 dash-scroll">
              {paymentHolds.map(client => {
                const primary = (client.contacts || []).find(c => c.is_primary) || (client.contacts || [])[0];
                return (
                  <div key={client.id} className="bg-white rounded p-2 border border-red-100 flex items-center justify-between cursor-pointer hover:bg-red-50/50 transition-colors"
                    onClick={() => navigate('/clients', { state: { openClientId: client.id } })} data-testid={`hold-client-${client.id}`}>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-medium text-slate-900">{client.name}</span>
                      {client.rating && <span className="px-1 py-0.5 rounded bg-slate-100 text-slate-500 font-medium">{client.rating}</span>}
                      {primary && <span className="text-slate-400">{primary.name}</span>}
                    </div>
                    <span className="text-xs text-red-400">›</span>
                  </div>
                );
              })}
            </div>
            )}
        </div>
      )}

      {immediateActions.length > 0 && (
          <div className="priority-list border-red-200 !h-[260px]" data-testid="immediate-actions">
            <div className="priority-list-header">
              <Zap className="w-4 h-4 text-red-500" />
              <h3 className="priority-list-title text-red-700">Immediate Action</h3>
              <span className="ml-2 text-xs font-medium bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{immediateActions.length}</span>
            </div>
            {!collapsedSections.immediateAction && (
            <div className="dash-scroll">
            {immediateActions.map((lead, index) => (
              <div key={lead.id} className="priority-item bg-red-50/50" data-testid={`immediate-action-item-${index}`}>
                <div
                  className="flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => navigate('/leads')}
                  data-testid={`immediate-action-link-${index}`}
                >
                  <div className="priority-item-title text-red-700 hover:underline">{lead.company_name}</div>
                  <div className="priority-item-subtitle">
                    {lead.contact_name}
                    {lead.phone && <span className="ml-2 text-slate-400">{lead.phone}</span>}
                    {lead.estimated_value > 0 && <span className="ml-2 font-medium text-slate-600">{formatCurrency(lead.estimated_value)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StagePill status={lead.status} />
                  <TempPill temperature={lead.temperature} />
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await updateLead(lead.id, { ...lead, immediate_action: false });
                        setImmediateActions(prev => prev.filter(l => l.id !== lead.id));
                        toast.success("Removed from immediate actions");
                      } catch { toast.error("Failed to update"); }
                    }}
                    data-testid={`immediate-done-${index}`}
                  >
                    Done
                  </Button>
                </div>
              </div>
            ))}
            </div>
            )}
          </div>
      )}
      </div>


      <div className="empty:hidden" data-testid="dash-row-service">
      {partsToShip.length > 0 && (
          <PartsToShipSection
            orders={partsToShip}
            navigate={navigate}
            onShip={async () => {
              // Reload parts-to-ship after status-button advances (ship modal / check payment)
              try {
                const res = await getPartsToShip();
                setPartsToShip(res.data || []);
              } catch { /* noop */ }
            }}
            onFoundationExport={async (o) => {
              try {
                const res = await exportPartsOrderToFoundation(o.id);
                toast.success(`Foundation ${res.data.foundation_invoice_number} generated`);
                setPartsToShip(prev => prev.map(p => p.id === o.id ? { ...p, foundation_invoice_number: res.data.foundation_invoice_number } : p));
              } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
            }}
          />
      )}
      </div>

      {/* Accounts Receivable */}
      {(allUnpaid.length > 0 || pendingInvoices.length > 0) && (
        <ARCard unpaid={unpaidInvoices} pending={pendingInvoices} onSend={handleSendInvoice} by={user?.name || user?.email || ""} onNotesChanged={loadDashboardData}
          onMarkPaid={(inv) => setPaymentTarget({ ...inv, amount: inv.total })} onRemind={handleSendReminder} onAutoChanged={patchUnpaid} />
      )}
      {paymentTarget && <PaymentDialog row={paymentTarget} onClose={() => setPaymentTarget(null)} onChanged={loadDashboardData} />}


      {/* Follow-Up Action Dialog */}
      <Dialog open={!!followUpAction} onOpenChange={(open) => { if (!open) setFollowUpAction(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {followUpAction?.type === "convert" ? "Convert to Customer" : "Complete Follow-Up"} - {followUpAction?.lead?.company_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Notes</Label>
              <Textarea
                value={followUpActionNotes}
                onChange={(e) => setFollowUpActionNotes(e.target.value)}
                rows={3}
                placeholder={followUpAction?.type === "convert" ? "Notes before converting to customer..." : "Notes about this follow-up..."}
                data-testid="input-followup-action-notes"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setFollowUpAction(null)}>Cancel</Button>
              <Button onClick={handleFollowUpAction} data-testid="btn-confirm-followup-action">
                {followUpAction?.type === "convert" ? "Convert to Customer" : "Mark as Done"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
    </CollapseContext.Provider>
  );
}

function PartsToShipSection({ orders, navigate, onShip, onFoundationExport }) {
  const [collapsed] = useSectionCollapse("partsToShip");
  return (
    <div className="priority-list" data-testid="parts-to-ship">
      <div className="priority-list-header">
        <Package className="w-4 h-4 text-amber-500" />
        <h3 className="priority-list-title">Parts To Ship</h3>
        <span className="ml-2 text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{orders.length}</span>
      </div>
      {!collapsed && (
      <div className="dash-scroll">
        {orders.map((o) => {
          const shipAddress = [o.ship_to_address, o.ship_to_city, o.ship_to_state, o.ship_to_zip].filter(Boolean).join(", ");
          return (
            <div key={o.id} className="priority-item" data-testid={`parts-ship-${o.id}`}>
              <div
                className="flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate("/clients", { state: { openPartsCounter: true } })}
              >
                <div className="priority-item-title text-blue-700 hover:underline">
                  {o.order_number} &mdash; {o.company}
                </div>
                <div className="priority-item-subtitle">
                  {o.contact ? `${o.contact} • ` : ""}{shipAddress || "Walk-in"}{o.paid_at ? ` • Paid ${format(new Date(o.paid_at), "MMM d")}` : ""}
                  {o.foundation_invoice_number ? ` • F: ${o.foundation_invoice_number}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="text-xs font-semibold text-slate-700 tabular-nums">
                  {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(o.total || 0)}
                </div>
                {!o.foundation_invoice_number && (
                  <Button size="sm" variant="outline" className="h-7 text-[11px] text-purple-600 border-purple-200 hover:bg-purple-50"
                    onClick={() => onFoundationExport(o)} data-testid={`dash-foundation-${o.id}`} title="Push to Foundation ERP">
                    Foundation
                  </Button>
                )}
                <PartsStatusButton order={o} onChanged={onShip} />
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}




function InspectionDeficienciesSection({ deficiencies, navigate, showDismissed, onToggleDismissed, onReload }) {
  const [collapsed] = useSectionCollapse("deficiencies");
  const [reasonFor, setReasonFor] = useState(null);
  const [reason, setReason] = useState("");
  const dismissGroup = async (group) => {
    try { await addDashboardDismissal({ kind: "deficiency", key: group.dismiss_key, reason, label: `${group.client_name} ${group.wo_number || ""} ${group.location_name || ""}`.trim() }); setReasonFor(null); setReason(""); onReload?.(); }
    catch { toast.error("Couldn't dismiss"); }
  };
  const restoreGroup = async (group) => {
    try { await removeDashboardDismissal("deficiency", group.dismiss_key); onReload?.(); } catch { toast.error("Couldn't restore"); }
  };
  // Group deficiencies by inspection
  const grouped = {};
  deficiencies.forEach(d => {
    const key = `${d.inspection_id}__${d.location_name}`;
    if (!grouped[key]) {
      grouped[key] = {
        inspection_id: d.inspection_id,
        template_name: d.template_name,
        client_id: d.client_id,
        client_name: d.client_name,
        dismissed: d.dismissed,
        dismiss_key: d.dismiss_key,
        wo_number: d.wo_number,
        work_order_id: d.work_order_id,
        site_name: d.site_name,
        scope_name: d.scope_name,
        location_name: d.location_name,
        location_id: d.location_id,
        conducted_date: d.conducted_date,
        items: [],
      };
    }
    grouped[key].items.push(d);
  });

  const groups = Object.values(grouped);

  const handleQuickProposal = async (group) => {
    try {
      const res = await createProposalFromDeficiencies({ items: group.items });
      const proposalId = res.data?.id;
      if (proposalId) {
        navigate("/proposals", { state: { viewProposalId: proposalId } });
      }
    } catch {
      // Fallback: navigate to proposals tab
      navigate("/proposals");
    }
  };

  const handleQuickWO = async (group) => {
    try {
      const res = await createWOFromDeficiencies({ items: group.items });
      const woId = res.data?.id;
      const clientId = res.data?.client_id || group.client_id;
      if (woId) {
        navigate("/clients", {
          state: {
            openClientId: clientId,
            openTab: "workorders",
            openWorkOrderId: woId,
          },
        });
      }
    } catch {
      navigate("/clients", { state: { openClientId: group.client_id, openTab: "workorders" } });
    }
  };

  return (
    <div className="priority-list" data-testid="inspection-deficiencies">
      <div className="priority-list-header">
        <AlertTriangle className="w-4 h-4 text-red-500" />
        <h3 className="priority-list-title">Inspection Deficiencies</h3>
        <span className="ml-2 text-xs font-medium bg-red-100 text-red-600 px-2 py-0.5 rounded-full">{deficiencies.filter(d => !d.dismissed).length}</span>
        <button type="button" className="ml-auto text-[11px] text-slate-500 hover:underline" onClick={onToggleDismissed} data-testid="deficiency-toggle-dismissed">{showDismissed ? "Hide dismissed" : "Show dismissed"}</button>
      </div>
      {!collapsed && (
      <div className="dash-scroll">
        {groups.length === 0 && <div className="px-4 py-3 text-xs text-slate-400" data-testid="deficiencies-empty">No open inspection deficiencies.</div>}
        {groups.map((group, gi) => (
          // eslint-disable-next-line react/no-array-index-key -- deficiency groups are aggregated by client/wo; identical client could appear twice if data fragmented
          <div key={`def-grp-${gi}-${group.client_name}`} className={`priority-item !py-1.5 flex-col items-start gap-1 ${group.dismissed ? "opacity-60" : ""}`} data-testid={`deficiency-group-${gi}`}>
            <div className="flex items-center justify-between w-full">
              <div className="flex-1 min-w-0 flex items-center gap-2 text-xs truncate">
                <span className="font-semibold text-slate-800 truncate">{group.client_name}</span>
                {group.wo_number && <span className="text-slate-400 font-mono shrink-0">WO# {group.wo_number}</span>}
                {group.location_name && <span className="text-slate-600 truncate" title={group.template_name}>{group.location_name}</span>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button variant="outline" size="sm" className="h-6 w-7 p-0" title="Proposal — create a quick proposal from these deficiencies" aria-label="Proposal"
                  onClick={() => handleQuickProposal(group)} data-testid={`deficiency-proposal-${gi}`}>
                  <FileText className="w-3.5 h-3.5" />
                </Button>
                <Button variant="outline" size="sm" className="h-6 w-7 p-0" title="WO — create a quick work order from these deficiencies" aria-label="WO"
                  onClick={() => handleQuickWO(group)} data-testid={`deficiency-wo-${gi}`}>
                  <Wrench className="w-3.5 h-3.5" />
                </Button>
                {group.dismissed ? (
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" title="Restore — show this on the dashboard again" onClick={() => restoreGroup(group)} data-testid={`deficiency-restore-${gi}`}>Restore</Button>
                ) : (
                  <Button variant="ghost" size="sm" className="h-6 w-7 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" title="Done — mark handled and hide from the dashboard" aria-label="Done" onClick={() => { setReasonFor(group.dismiss_key); setReason(""); }} data-testid={`deficiency-dismiss-${gi}`}>
                    <Check className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
            {reasonFor === group.dismiss_key && (
              <div className="w-full flex items-center gap-2 rounded border border-slate-200 bg-slate-50 p-2" data-testid={`deficiency-dismiss-form-${gi}`}>
                <input value={reason} onChange={e => setReason(e.target.value)} placeholder="How was it handled? (optional)" className="flex-1 h-7 text-xs px-2 rounded border border-slate-200 bg-white" data-testid={`deficiency-dismiss-reason-${gi}`} />
                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => dismissGroup(group)} data-testid={`deficiency-dismiss-confirm-${gi}`}>Mark Done</Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setReasonFor(null)}>Cancel</Button>
              </div>
            )}
            <div className="w-full pl-1 space-y-0">
              {group.items.map((item, ii) => (
                // eslint-disable-next-line react/no-array-index-key -- deficiency items rendered in original inspection response order
                <div key={`def-itm-${ii}-${item.checkpoint_label || ''}`} className="flex items-center gap-1.5 text-[11px] leading-4" data-testid={`deficiency-item-${gi}-${ii}`}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                  <div className="min-w-0 truncate">
                    <span className="font-medium text-slate-700">{(item.section_name || "").replace(/:+\s*$/, "")}:</span>{" "}
                    <span className="text-slate-600">{item.checkpoint_label}</span>
                    {item.notes && <span className="text-slate-400 italic ml-1">— {item.notes}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}


function ToSendTable({ invoices, onSend, onPayment }) {
  if (!invoices.length) return <div className="h-[352px] px-4 py-3 text-xs text-slate-400">No invoices waiting to be sent.</div>;
  return (
    <div className="h-[352px] overflow-y-auto">
      <table className="w-full text-xs" data-testid="to-send-table">
        <thead className="sticky top-0 z-10 bg-white">
          <tr className="h-8 text-left text-[10px] uppercase text-slate-400 border-b">
            <th className="pl-4 pr-2">Invoice</th><th className="px-2">Customer</th><th className="px-2">Name</th><th className="px-2" /><th className="px-2 text-right">Amount</th><th className="pl-2 pr-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map(inv => (
            <tr key={inv.id} className="h-9 border-b border-slate-100 hover:bg-slate-50" data-testid={`pending-invoice-${inv.id}`}>
              <td className="pl-4 pr-2 font-mono font-semibold text-slate-700 whitespace-nowrap">{inv.invoice_number}</td>
              <td className="px-2 text-slate-600 truncate max-w-[420px]" title={inv.customer_name || ""}>{inv.customer_name || "—"}</td>
              <td className="px-2 text-slate-600 truncate max-w-[320px]" title={inv.job_name || ""}>{inv.job_name || "—"}</td>
              <td className="px-2 text-orange-500 whitespace-nowrap">{inv.auto_generated ? "Auto-generated" : ""}</td>
              <td className="px-2 text-right font-semibold text-slate-800 num">{formatCurrency(inv.total)}</td>
              <td className="pl-2 pr-4 text-right whitespace-nowrap" data-testid={`send-invoice-btn-${inv.id}`}>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Send invoice" aria-label="Send invoice" onClick={() => onSend(inv)} data-testid={`btn-send-${inv.id}`}>
                  <Mail className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-700" title="Record payment" aria-label="Record payment" onClick={() => onPayment(inv)} data-testid={`btn-payment-${inv.id}`}>
                  <CircleDollarSign className="w-3.5 h-3.5" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const AR_BUCKETS = [
  { key: "0_30", label: "0-30 days", color: "text-blue-600", ring: "ring-blue-400" },
  { key: "31_60", label: "31-60 days", color: "text-amber-600", ring: "ring-amber-400" },
  { key: "60_plus", label: "60+ days", color: "text-red-600", ring: "ring-red-400" },
];
const TO_SEND = { key: "to_send", label: "To send", color: "text-orange-600", ring: "ring-orange-400" };
const owed = (i) => (i.balance != null && i.amount_paid > 0 ? i.balance : (i.total || 0));
const sumTotal = (items) => items.reduce((s, i) => s + owed(i), 0);
const shortDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

function ARCard({ unpaid, pending, onSend, by, onNotesChanged, onMarkPaid, onRemind, onAutoChanged }) {
  const [collapsed] = useSectionCollapse("ar");
  const oldest = [...AR_BUCKETS].reverse().find(b => (unpaid[b.key] || []).length)?.key || (pending.length ? "to_send" : "0_30");
  const [bucket, setBucket] = useState(oldest);
  const all = AR_BUCKETS.flatMap(b => unpaid[b.key] || []);
  const rows = unpaid[bucket] || [];
  return (
    <div className="priority-list" data-testid="unpaid-invoices">
      <div className="priority-list-header">
        <Wallet className="w-4 h-4 text-green-500" />
        <h3 className="priority-list-title">Accounts Receivable</h3>
        <span className="ml-2 text-xs font-medium bg-green-100 text-green-600 px-2 py-0.5 rounded-full">{all.length}</span>
        <span className="ml-auto text-sm font-bold text-slate-700 num" data-testid="ar-total">{formatCurrency(sumTotal(all))}</span>
      </div>
      {!collapsed && (<>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 p-3 border-b border-slate-200" data-testid="ar-summary">
          {[TO_SEND, ...AR_BUCKETS].map(b => {
            const items = b.key === "to_send" ? pending : (unpaid[b.key] || []);
            return (
              <button key={b.key} type="button" onClick={() => setBucket(b.key)} data-testid={`ar-bucket-${b.key}`}
                className={`rounded-lg border border-slate-200 px-3 py-2 text-left transition-colors hover:bg-slate-50 ${bucket === b.key ? `ring-2 ${b.ring}` : ""}`}>
                <div className={`text-[10px] font-semibold uppercase tracking-wide ${b.color}`}>{b.label}</div>
                <div className="text-base font-bold text-slate-900 num">{formatCurrency(sumTotal(items))}</div>
                <div className="text-[10px] text-slate-400">{items.length} invoice{items.length !== 1 ? "s" : ""}</div>
              </button>
            );
          })}
        </div>
        {bucket === "to_send" ? <ToSendTable invoices={pending} onSend={onSend} onPayment={onMarkPaid} /> : <ARTable rows={rows} by={by} onNotesChanged={onNotesChanged} onMarkPaid={onMarkPaid} onRemind={onRemind} onAutoChanged={onAutoChanged} />}
      </>)}
    </div>
  );
}

function ARTable({ rows, by, onNotesChanged, onMarkPaid, onRemind, onAutoChanged }) {
  if (!rows.length) return <div className="h-[352px] px-4 py-3 text-xs text-slate-400">No invoices in this age group.</div>;
  return (
    <div className="h-[352px] overflow-y-auto">
      <table className="w-full text-xs" data-testid="ar-table">
        <thead className="sticky top-0 z-10 bg-white">
          <tr className="h-8 text-left text-[10px] uppercase text-slate-400 border-b">
            <th className="pl-4 pr-2">Invoice</th><th className="px-2">Customer</th><th className="px-2">Name</th>
            <th className="px-2">Sent</th><th className="px-2">Last activity</th><th className="px-2 text-right w-24">Amount</th><th className="px-2 text-center w-16">Notes</th><th className="pl-2 pr-4 text-right w-28">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(inv => (
            <ARRow key={inv.id} inv={inv} by={by} onNotesChanged={onNotesChanged} onMarkPaid={() => onMarkPaid(inv)} onRemind={() => onRemind(inv)} onAutoChanged={onAutoChanged} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ARRow({ inv, by, onNotesChanged, onMarkPaid, onRemind, onAutoChanged }) {
  const activity = inv.ar_last_action || (inv.last_reminder_tier > 0 ? `Reminder #${inv.last_reminder_tier}` : "");
  return (
    <tr className="h-8 border-b border-slate-100 hover:bg-slate-50" data-testid={`ar-invoice-${inv.id}`}>
      <td className="pl-4 pr-2 font-mono font-semibold text-slate-700 whitespace-nowrap">{inv.invoice_number}</td>
      <td className="px-2 text-slate-600 truncate max-w-[420px]" title={inv.customer_name || ""}>{inv.customer_name || "—"}</td>
      <td className="px-2 text-slate-600 truncate max-w-[320px]" title={inv.job_name || ""}>{inv.job_name || "—"}</td>
      <td className="px-2 text-slate-500 whitespace-nowrap">{inv.status === "sent" ? shortDate(inv.sent_at) : "Draft"}</td>
      <td className="px-2 text-slate-500 whitespace-nowrap">{activity || "—"}</td>
      <td className="px-2 text-right font-semibold text-slate-800 num" title={inv.amount_paid > 0 ? `Total ${formatCurrency(inv.total)} — paid ${formatCurrency(inv.amount_paid)}` : undefined}>{formatCurrency(owed(inv))}</td>
      <td className="px-2 text-center"><InvoiceNotesCell row={inv} by={by} onChanged={onNotesChanged} /></td>
      <td className="pl-2 pr-4 text-right whitespace-nowrap">
        <InvoiceActionButtons r={inv} onRemind={onRemind} onPayment={onMarkPaid} onChanged={onAutoChanged} />
      </td>
    </tr>
  );
}
